import {useEffect, useMemo, useState} from "react";
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, FormControl, Grid, IconButton, InputLabel,
    Link, MenuItem, Paper, Select, Tooltip, Typography
} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import useApiCall from "../../hooks/CancellableApiCall";
import {LoadingContent} from "../LoadingContent";
import {InvestigatorOverviewEntry, InvestigatorOverviewComplaint} from "../../data/internal-affairs/InvestigatorOverviewResponse";
import {InternalAffairsComplaintStatus} from "../../data/internal-affairs/InternalAffairsComplaintStatus";
import {InternalAffairsComplaintClassification} from "../../data/internal-affairs/InternalAffairsComplaintClassification";
import useUser from "../../hooks/useUser";

const statusColors: Record<string, string> = {
    awaiting_investigator: "#e65100",
    investigator_assigned: "#1565c0",
    pending_statement_response: "#6a1b9a",
    pending_creator_response: "#4527a0",
    complaint_frozen: "#37474f",
    ia_cmd_review: "#00695c",
    junior_cc_review: "#2e7d32",
    senior_cc_review: "#1b5e20",
    final_reported: "#33691e",
    re_voting: "#f9a825",
    closed: "#424242",
}

// ── reassign modal ───────────────────────────────────────────────────────────

function ReassignModal({displayId, investigators, currentAssigneeCommunityId, defendantCommunityId, onAssigned, open, onClose}: {
    displayId: string
    investigators: InvestigatorOverviewEntry[]
    currentAssigneeCommunityId: string
    defendantCommunityId: string
    onAssigned: () => void
    open: boolean
    onClose: () => void
}) {
    const [selected, setSelected] = useState("")
    const [loading, setLoading] = useState(false)

    async function submit() {
        if (!selected) return
        setLoading(true)
        try {
            await fetch(`/api/internal-affairs/complaint/${displayId}/reassign`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({fromCommunityId: currentAssigneeCommunityId, toCommunityId: selected})
            })
            onAssigned()
            onClose()
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Reassign {displayId}</DialogTitle>
            <DialogContent>
                <FormControl fullWidth size="small" sx={{mt: 1}}>
                    <InputLabel>Investigator</InputLabel>
                    <Select value={selected} label="Investigator" onChange={e => setSelected(e.target.value as string)}>
                        {investigators
                            .filter(i => i.communityId !== currentAssigneeCommunityId && i.communityId !== "-1" && i.communityId !== defendantCommunityId)
                            .map(i => (
                                <MenuItem key={i.communityId} value={i.communityId}>
                                    {i.nick} — {i.complaints.length} complaint{i.complaints.length !== 1 ? "s" : ""}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button variant="contained" disabled={!selected || loading} onClick={submit}>Reassign</Button>
            </DialogActions>
        </Dialog>
    )
}

// ── inline assign (unassigned row) ───────────────────────────────────────────

function InlineAssign({displayId, investigators, onAssigned}: {
    displayId: string
    investigators: InvestigatorOverviewEntry[]
    onAssigned: () => void
}) {
    const [selected, setSelected] = useState<string[]>([])
    const [loading, setLoading] = useState(false)

    async function assign() {
        if (selected.length === 0) return
        setLoading(true)
        try {
            for (const communityId of selected) {
                await fetch(`/api/internal-affairs/complaint/${displayId}/assign`, {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({assigneeCommunityId: communityId})
                })
            }
            onAssigned()
        } finally {
            setLoading(false)
            setSelected([])
        }
    }

    const available = investigators.filter(i => i.communityId !== "-1" && !selected.includes(i.communityId))

    return (
        <Box sx={{display: "flex", gap: 1, alignItems: "center", minWidth: 0, flexWrap: "wrap"}}>
            {selected.map(id => {
                const inv = investigators.find(i => i.communityId === id)
                return (
                    <Chip key={id} label={inv?.nick ?? id} size="small"
                          onDelete={() => setSelected(selected.filter(s => s !== id))}/>
                )
            })}
            <FormControl size="small" sx={{minWidth: 160}}>
                <InputLabel>Select a user...</InputLabel>
                <Select value="" label="Select a user..." onChange={e => {
                    const val = e.target.value as string
                    if (val && !selected.includes(val)) setSelected([...selected, val])
                }}>
                    {available.map(i => (
                        <MenuItem key={i.communityId} value={i.communityId}>
                            {i.nick} ({i.complaints.length})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button size="small" variant="contained" disabled={selected.length === 0 || loading} onClick={assign}>
                Assign
            </Button>
        </Box>
    )
}

// ── complaint line (inside investigator card) ────────────────────────────────

interface ActivityItem { message: string; date: string; nick: string }

function ActivityTooltipContent({displayId}: { displayId: string }) {
    const [activities, setActivities] = useState<ActivityItem[] | null>(null)

    useEffect(() => {
        let cancelled = false
        setActivities(null)
        fetch(`/api/internal-affairs/complaint/${displayId}/activities`)
            .then(r => r.json())
            .then(data => { if (!cancelled) setActivities(data) })
            .catch(() => { if (!cancelled) setActivities([]) })
        return () => { cancelled = true }
    }, [displayId])

    if (!activities) return <Typography variant="caption">Loading...</Typography>
    if (activities.length === 0) return <Typography variant="caption">No activity</Typography>
    return (
        <Box sx={{maxHeight: 200, overflow: "auto"}}>
            {activities.map((a, i) => (
                <Box key={i} sx={{py: 0.3, "&:not(:last-child)": {borderBottom: "1px solid rgba(255,255,255,0.1)"}}}>
                    <Typography variant="caption" display="block">
                        {a.nick} — {a.message}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                        {new Date(a.date).toLocaleDateString()}
                    </Typography>
                </Box>
            ))}
        </Box>
    )
}

function ComplaintLine({c, assigneeCommunityId, allInvestigators, canAssign, onAssigned}: {
    c: InvestigatorOverviewComplaint
    assigneeCommunityId: string
    allInvestigators: InvestigatorOverviewEntry[]
    canAssign: boolean
    onAssigned: () => void
}) {
    const [reassignOpen, setReassignOpen] = useState(false)
    const statusLabel = InternalAffairsComplaintStatus.get(c.status.toLowerCase())?.displayName ?? c.status
    const classLabel  = InternalAffairsComplaintClassification.get(c.classification.toLowerCase())?.displayName ?? c.classification
    const ageDays     = Math.floor((Date.now() - new Date(c.creationDate).getTime()) / 86400000)

    return (
        <>
            <Tooltip
                title={<ActivityTooltipContent displayId={c.displayId}/>}
                placement="bottom-start"
                enterDelay={300}
                arrow
            >
            <Box sx={{py: 1, "&:not(:last-child)": {borderBottom: 1, borderColor: "divider"}}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <Link underline="none" component={RouterLink} to={`/complaint/${c.displayId}`}
                          variant="body2" fontWeight={600} sx={{fontFamily: "monospace", flexShrink: 0}}>
                        {c.displayId}
                    </Link>
                    <Typography variant="body2" sx={{flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                        {c.defendantName}
                    </Typography>
                    <Typography variant="caption" color={c.overdue ? "warning.main" : "text.secondary"} sx={{flexShrink: 0}}>
                        {ageDays}d
                    </Typography>
                    {c.overdue && <WarningAmberIcon sx={{fontSize: 14, color: "warning.main", flexShrink: 0, verticalAlign: "middle", display: "flex", alignSelf: "center"}}/>}
                    {canAssign && (
                        <Tooltip title="Reassign">
                            <IconButton size="small" onClick={() => setReassignOpen(true)} sx={{flexShrink: 0}}>
                                <SwapHorizIcon sx={{fontSize: 16}}/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Box sx={{display: "flex", gap: 0.75, mt: 0.5, alignItems: "center"}}>
                    <Chip label={statusLabel} size="small" variant="filled"
                          sx={{fontSize: "0.68rem", height: 20, backgroundColor: statusColors[c.status.toLowerCase()] ?? "#616161", color: "#fff"}}/>
                    <Typography variant="caption" color="text.secondary">{classLabel}</Typography>
                </Box>
            </Box>
            </Tooltip>

            {canAssign && (
                <ReassignModal
                    open={reassignOpen}
                    onClose={() => setReassignOpen(false)}
                    displayId={c.displayId}
                    investigators={allInvestigators}
                    currentAssigneeCommunityId={assigneeCommunityId}
                    defendantCommunityId={c.defendantCommunityId}
                    onAssigned={onAssigned}
                />
            )}
        </>
    )
}

// ── unassigned complaint line (full-width row) ───────────────────────────────

function UnassignedComplaintLine({c, allInvestigators, canAssign, onAssigned}: {
    c: InvestigatorOverviewComplaint
    allInvestigators: InvestigatorOverviewEntry[]
    canAssign: boolean
    onAssigned: () => void
}) {
    const statusLabel = InternalAffairsComplaintStatus.get(c.status.toLowerCase())?.displayName ?? c.status
    const classLabel  = InternalAffairsComplaintClassification.get(c.classification.toLowerCase())?.displayName ?? c.classification
    const ageDays     = Math.floor((Date.now() - new Date(c.creationDate).getTime()) / 86400000)

    return (
        <Box sx={{
            display: "flex", alignItems: "center", gap: 2, py: 1,
            flexWrap: "wrap",
            "&:not(:last-child)": {borderBottom: 1, borderColor: "divider"}
        }}>
            <Link underline="none" component={RouterLink} to={`/complaint/${c.displayId}`}
                  variant="body2" fontWeight={600} sx={{fontFamily: "monospace", flexShrink: 0}}>
                {c.displayId}
            </Link>
            <Typography variant="body2" sx={{flexShrink: 0}}>{c.defendantName}</Typography>
            <Chip label={statusLabel} size="small" variant="filled"
                  sx={{fontSize: "0.68rem", height: 20, backgroundColor: statusColors[c.status.toLowerCase()] ?? "#616161", color: "#fff"}}/>
            <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>{classLabel}</Typography>
            <Box sx={{flex: 1}}/>
            <Typography variant="caption" color={c.overdue ? "warning.main" : "text.secondary"} sx={{flexShrink: 0}}>
                {ageDays}d {c.overdue && "⚠"}
            </Typography>
            {canAssign && (
                <InlineAssign displayId={c.displayId} investigators={allInvestigators} onAssigned={onAssigned}/>
            )}
        </Box>
    )
}

// ── investigator card ────────────────────────────────────────────────────────

function InvestigatorCard({entry, allInvestigators, canAssign, onAssigned}: {
    entry: InvestigatorOverviewEntry
    allInvestigators: InvestigatorOverviewEntry[]
    canAssign: boolean
    onAssigned: () => void
}) {
    const overdueCount = entry.complaints.filter(c => c.overdue).length

    return (
        <Paper variant="outlined" sx={{display: "flex", flexDirection: "column", height: "100%"}}>
            <Box sx={{px: 2, py: 1.5, display: "flex", alignItems: "flex-start", gap: 1}}>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Link underline="none" component={RouterLink} to={`/user/${entry.communityId}`}
                          variant="subtitle2" fontWeight={700} noWrap display="block">
                        {entry.nick}
                    </Link>
                    {entry.rankDisplayName && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {entry.iaRoleDisplayName ?? entry.rankDisplayName}
                        </Typography>
                    )}
                    {entry.onLeave && (
                        <Chip label="On Leave" size="small" color="warning" variant="outlined" sx={{fontSize: "0.65rem", height: 18, mt: 0.25}}/>
                    )}
                </Box>
                <Box sx={{display: "flex", gap: 0.5, flexShrink: 0, alignItems: "center", pt: 0.25}}>
                    {overdueCount > 0 && (
                        <Chip label={`⚠ ${overdueCount}`} size="small" color="warning" variant="outlined"/>
                    )}
                    <Chip
                        label={entry.complaints.length}
                        size="small"
                        color={entry.complaints.length === 0 ? "default" : "primary"}
                        variant={entry.complaints.length === 0 ? "outlined" : "filled"}
                    />
                </Box>
            </Box>
            <Divider/>
            <Box sx={{px: 2, flex: 1, ...(entry.complaints.length > 3 && {maxHeight: 280, overflowY: "auto"})}}>
                {entry.complaints.length === 0 ? (
                    <Typography variant="body2" color="text.disabled" sx={{py: 1.5}}>No active complaints</Typography>
                ) : (
                    entry.complaints.map(c => (
                        <ComplaintLine
                            key={c.displayId}
                            c={c}
                            assigneeCommunityId={entry.communityId}
                            allInvestigators={allInvestigators}
                            canAssign={canAssign}
                            onAssigned={onAssigned}
                        />
                    ))
                )}
            </Box>
        </Paper>
    )
}

// ── unassigned row ───────────────────────────────────────────────────────────

function UnassignedRow({entry, allInvestigators, canAssign, onAssigned}: {
    entry: InvestigatorOverviewEntry
    allInvestigators: InvestigatorOverviewEntry[]
    canAssign: boolean
    onAssigned: () => void
}) {
    return (
        <Paper variant="outlined" sx={{borderColor: "warning.main"}}>
            <Box sx={{px: 2, py: 1.25, display: "flex", alignItems: "center", gap: 1}}>
                <Typography variant="subtitle2" fontWeight={700} color="warning.main">Unassigned</Typography>
                <Chip label={entry.complaints.length} size="small" color="warning" variant="outlined"/>
            </Box>
            <Divider/>
            <Box sx={{px: 2}}>
                {entry.complaints.map(c => (
                    <UnassignedComplaintLine
                        key={c.displayId}
                        c={c}
                        allInvestigators={allInvestigators}
                        canAssign={canAssign}
                        onAssigned={onAssigned}
                    />
                ))}
            </Box>
        </Paper>
    )
}

// ── panel ────────────────────────────────────────────────────────────────────

export const InvestigatorOverviewPanel = () => {
    const data = useApiCall<InvestigatorOverviewEntry[]>({url: "/api/internal-affairs/investigator-overview"})
    const user = useUser()
    const canAssign = user.hasPermissions(["internalAffairs:assign"])

    const [roleFilter, setRoleFilter] = useState("all")
    const [overdueFilter, setOverdueFilter] = useState("all")

    const roles = useMemo(() => {
        if (!data.data) return []
        const seen = new Set<string>()
        return data.data
            .flatMap(e => e.iaRoleDisplayName ? [e.iaRoleDisplayName] : [])
            .filter(r => { const n = !seen.has(r); seen.add(r); return n })
    }, [data.data])

    const {unassigned, investigators} = useMemo(() => {
        if (!data.data) return {unassigned: null, investigators: []}
        const filtered = data.data.filter(e => {
            if (e.communityId !== "-1" && roleFilter !== "all" && e.iaRoleDisplayName !== roleFilter) return false
            if (overdueFilter === "overdue" && !e.complaints.some(c => c.overdue)) return false
            if (overdueFilter === "clean" && e.complaints.some(c => c.overdue)) return false
            return true
        })
        return {
            unassigned: filtered.find(e => e.communityId === "-1") ?? null,
            investigators: filtered.filter(e => e.communityId !== "-1")
        }
    }, [data.data, roleFilter, overdueFilter])

    const totalComplaints = (unassigned?.complaints.length ?? 0) + investigators.reduce((s, e) => s + e.complaints.length, 0)
    const totalOverdue = (unassigned?.complaints.filter(c => c.overdue).length ?? 0) + investigators.reduce((s, e) => s + e.complaints.filter(c => c.overdue).length, 0)

    return (
        <LoadingContent isLoading={data.isLoading || !data.data}>
            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>

                <Box sx={{display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center"}}>
                    <FormControl size="small" sx={{minWidth: 220}}>
                        <InputLabel>Role</InputLabel>
                        <Select value={roleFilter} label="Role" onChange={e => setRoleFilter(e.target.value)}>
                            <MenuItem value="all">All roles</MenuItem>
                            {roles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{minWidth: 160}}>
                        <InputLabel>Overdue</InputLabel>
                        <Select value={overdueFilter} label="Overdue" onChange={e => setOverdueFilter(e.target.value)}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="overdue">Has overdue</MenuItem>
                            <MenuItem value="clean">No overdue</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{flex: 1}}/>
                    <Typography variant="body2" color="text.secondary">
                        {investigators.length} investigator{investigators.length !== 1 ? "s" : ""} · {totalComplaints} complaint{totalComplaints !== 1 ? "s" : ""}
                        {totalOverdue > 0 && <> · <Box component="span" sx={{color: "warning.main"}}>{totalOverdue} overdue</Box></>}
                    </Typography>
                </Box>

                {unassigned && unassigned.complaints.length > 0 && (
                    <UnassignedRow
                        entry={unassigned}
                        allInvestigators={data.data!}
                        canAssign={canAssign}
                        onAssigned={() => data.refresh?.()}
                    />
                )}

                {investigators.length === 0
                    ? <Alert severity="info">No investigators match the current filters.</Alert>
                    : (
                        <Grid container spacing={2} alignItems="stretch">
                            {investigators.map(e => (
                                <Grid item key={e.communityId} xs={12} sm={6} md={4} lg={3}>
                                    <InvestigatorCard
                                        entry={e}
                                        allInvestigators={data.data!}
                                        canAssign={canAssign}
                                        onAssigned={() => data.refresh?.()}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )
                }
            </Box>
        </LoadingContent>
    )
}
